import {
  ArrowLeft,
  CheckSquare,
  ChevronRight,
  Code,
  Edit2,
  ExternalLink,
  FileText,
  GripVertical,
  Image,
  Link,
  type LucideIcon,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
  Video,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from '#router'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { IconField } from '@/components/admin/shared/icon-field'
import {
  containerFallbackIcon,
  leafKindIcon,
} from '@/components/admin/shared/kind-icons'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { mergeChildren } from '@/lib/coaching/source-tree'
import { getDifficultyColorClass, getDifficultyLabel } from '@/lib/difficulty'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { toastError, toastSuccess } from '@/lib/utils/toast-helpers'
import { useProblemSearch } from '../problems/hooks/use-problem-search'
import { useCoachingQuestions } from '../problems/hooks/use-questions'
import { SOLUTION_TYPE_CONFIG } from '../problems/master-questions-table/constants'
import type { CoachingQuestion } from '../problems/types'
import type { SystemDesignProblem } from '../system-design/hooks/use-system-design-problems'
import { useSystemDesignProblems } from '../system-design/hooks/use-system-design-problems'
import { useSystemDesignSearch } from '../system-design/hooks/use-system-design-search'
import { useTaskSearch } from '../tasks/hooks/use-task-search'
import { useTasks } from '../tasks/hooks/use-tasks'
import { TaskEditorModal } from '../tasks/task-editor-modal'
import type { Task, TaskFormData } from '../tasks/types'
import { TASK_CATEGORIES } from '../tasks/types'
import {
  useReorderChildren,
  useSourceContent,
  useSourceContentItems,
} from './hooks/use-sources'
import {
  SDSourceLinkModal,
  type SDSourceLinkSubmitData,
} from './sd-source-link-modal'
import { SourceContentModal } from './source-content-modal'
import type {
  SourceContent,
  SourceContentChild,
  SourceContentItem,
} from './types'
import { SOURCE_CONTENT_ITEM_TYPES, SOURCE_CONTENT_TYPES } from './types'
import { YouTubeImportModal } from './youtube-import-modal'

/**
 * Resource-kind items no longer arrive as contentType VIDEO/ARTICLE on read
 * (that distinction now lives on `resource.type`) — this restores the old
 * per-format icon (video vs. article) instead of the generic RESOURCE icon.
 *
 * That refinement stays here rather than in `leafKindIcon` because it reads
 * `resource.type`, which is this page's data rather than shared vocabulary;
 * everything else defers to the one leaf-kind table.
 */
function getItemIcon(item: SourceContentItem): LucideIcon {
  if (item.contentType === 'RESOURCE') {
    if (item.resource?.type === 'video') return Video
    if (item.resource?.type === 'article') return FileText
    return ExternalLink
  }
  return leafKindIcon(item.contentType)
}

/**
 * Renders the leading icon for a source-content row. Selection priority
 * mirrors the original: the item's own authored icon first, else the
 * problem's source icon, falling back to the type icon — that priority is
 * this page's own knowledge and is preserved here. What changed is *how* the
 * chosen value becomes a picture: it's routed through `AuthoredIcon` so an
 * authored emoji or a bundled filename (previously only an absolute URL)
 * renders correctly, instead of silently rendering nothing.
 */
function SourceContentItemIcon({
  itemIcon,
  problemIcon,
  FallbackIcon,
}: {
  itemIcon: string | null
  problemIcon: string | null | undefined
  FallbackIcon: LucideIcon
}) {
  // The priority is a *chain*, not a pick: the problem's icon is the item
  // icon's fallback rather than an alternative to it, so an item icon that is
  // blank once trimmed, or that 404s, still lands on the problem's icon
  // instead of skipping past it to the type mark.
  //
  // AuthoredIcon walks the chain itself, so this states the priority once
  // instead of nesting a second component to express "and then try this".
  return (
    <AuthoredIcon
      icon={[itemIcon, problemIcon]}
      fallback={FallbackIcon}
      className="text-muted-foreground flex-shrink-0"
    />
  )
}

/**
 * The source's icon for a problem-search result row, falling back to a
 * generic code mark. Takes the raw `icon` value rather than a URL so that an
 * authored emoji renders as itself instead of as a broken image.
 *
 * The row names its source nowhere else, so unlike every other icon on this
 * page this one needs an accessible name — which `AuthoredIcon` supplies from
 * `label`, since it is the module that made the mark decorative.
 */
function SearchResultIcon({
  icon,
  sourceName,
}: {
  icon: string | null | undefined
  sourceName: string
}) {
  return (
    <AuthoredIcon
      icon={icon}
      size="sm"
      label={sourceName || undefined}
      className="text-muted-foreground flex-shrink-0"
      fallback={Code}
    />
  )
}

function ContainerChildRow({
  child,
  isDragged,
  onNavigate,
  onEdit,
  onDelete,
  dragProps,
}: {
  child: SourceContentChild
  isDragged: boolean
  onNavigate: () => void
  onEdit: () => void
  onDelete: () => void
  dragProps: {
    onDragStart: (e: React.DragEvent) => void
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
    onDragEnd: () => void
  }
}) {
  const KindIcon = containerFallbackIcon(child.kind)
  return (
    <div
      draggable
      {...dragProps}
      className={`group flex cursor-pointer items-center gap-3 p-4 transition-colors ${
        isDragged ? 'bg-muted/50 opacity-50' : 'hover:bg-muted/30'
      }`}
      {...activatableRowProps({
        onActivate: onNavigate,
        label: `Open ${child.title}`,
      })}
    >
      <div className={cn(REVEAL_ON_HOVER, 'text-muted-foreground cursor-grab')}>
        <GripVertical size={16} />
      </div>
      <KindIcon size={18} className="text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground truncate font-medium">
            {child.title}
          </span>
          <span className="bg-muted text-muted-foreground flex-shrink-0 rounded px-1.5 py-0.5 text-xs">
            {child.kind}
          </span>
          {child.itemCount > 0 && (
            <span className="text-muted-foreground text-xs">
              {child.itemCount} item{child.itemCount !== 1 ? 's' : ''}
            </span>
          )}
          {child.estimatedMinutes != null && child.estimatedMinutes > 0 && (
            <span className="text-muted-foreground text-xs">
              {child.estimatedMinutes} min
            </span>
          )}
        </div>
        {child.description && (
          <p className="text-muted-foreground mt-0.5 truncate text-sm">
            {child.description}
          </p>
        )}
      </div>
      <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-2')}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          className="text-muted-foreground hover:text-foreground text-sm"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <ChevronRight size={16} className="text-muted-foreground" />
    </div>
  )
}

// Attach handlers compute the appended order from mixedChildren, which is
// incomplete until the items query settles — attaching then would land the
// new row mid-sequence (and bypass the already-added dedup filters).
const ITEMS_LOADING_MESSAGE =
  'This container is still loading — try again in a moment'

export function SourceContentDetail() {
  const params = useParams()
  const { navigate } = useLocation()
  const sourceId = params.sourceId as string
  const contentId = params.contentId as string

  // Content metadata from tRPC
  const {
    data: content,
    isLoading: loading,
    error: queryError,
  } = trpc.admin.content.getSourceContent.useQuery(
    { id: contentId },
    { enabled: !!contentId },
  )
  const error = queryError ? queryError.message : null

  // Items management
  const {
    items,
    loading: itemsLoading,
    addItem,
    updateItem,
    deleteItem,
    refresh: refreshItems,
  } = useSourceContentItems(contentId)

  // The source's containers: parent-picker options, breadcrumb, child rows
  const { contents, loading: contentsLoading } = useSourceContent(sourceId)
  const { reorderChildren, invalidateChildViews, isReordering } =
    useReorderChildren()
  const [isAddChildOpen, setIsAddChildOpen] = useState(false)
  const [editingChild, setEditingChild] = useState<SourceContent | null>(null)

  // Merged sequence of sub-containers and items, in server display order
  const mixedChildren = useMemo(
    () => mergeChildren(content?.children ?? [], items),
    [content?.children, items],
  )

  // Ancestor chain for the breadcrumb, root-first
  const ancestors = useMemo(() => {
    const byId = new Map(contents.map((c) => [c.id, c]))
    const chain: SourceContent[] = []
    let cursor = content?.parentId ? byId.get(content.parentId) : undefined
    let guard = 0
    while (cursor && guard < 100) {
      chain.unshift(cursor)
      cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined
      guard += 1
    }
    return chain
  }, [contents, content?.parentId])

  // Problem search for fuzzy finder
  const { questions, loading: questionsLoading } = useCoachingQuestions()
  const { search, minSearchLength } = useProblemSearch(questions)

  // Task search for fuzzy finder
  const {
    tasks,
    loading: tasksLoading,
    createTask,
    updateTask,
    refresh: refreshTasks,
  } = useTasks()
  const { search: searchTasks, minSearchLength: minTaskSearchLength } =
    useTaskSearch(tasks, { prioritizeSourceId: sourceId })

  // System design search for fuzzy finder
  const { problems: sdProblems, loading: sdLoading } = useSystemDesignProblems()
  const { search: searchSD, minSearchLength: minSDSearchLength } =
    useSystemDesignSearch(sdProblems)

  // Source name lookup
  const { data: allSources } = trpc.admin.sources.list.useQuery()
  const sourceName =
    allSources?.find((s) => s.id === sourceId)?.name ?? 'Source'

  // Declared up here rather than beside the delete mutation below, because the
  // SD source-link mutations need it and a `useCallback` deps array further
  // down evaluates during render — a later `const` would be in its TDZ.
  const utils = trpc.useUtils()

  // SD source link mutations. Both invalidate the read that decides
  // create-vs-update for this pair: nothing else invalidates that key, and with
  // the app-wide 30s staleTime the add flow otherwise caches `{source: null}`,
  // so re-opening the same item inside that window shows a blank *create* form
  // for a link that exists — and submitting it creates a second attribution row.
  const invalidateSDSourceLink = useCallback(
    () => utils.admin.content.getSystemDesignProblemSource.invalidate(),
    [utils],
  )
  const createSDSourceMutation =
    trpc.admin.content.createSystemDesignProblemSource.useMutation({
      onSuccess: invalidateSDSourceLink,
    })
  const updateSDSourceMutation =
    trpc.admin.content.updateSystemDesignProblemSource.useMutation({
      onSuccess: invalidateSDSourceLink,
    })

  // Create a lookup map for quick access to full problem data
  const problemsById = useMemo(() => {
    const map = new Map<string, CoachingQuestion>()
    for (const q of questions) {
      map.set(q.id, q)
    }
    return map
  }, [questions])

  // Search state for problems
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Search state for tasks
  const [taskSearchQuery, setTaskSearchQuery] = useState('')
  const [isTaskSearchFocused, setIsTaskSearchFocused] = useState(false)

  // Search state for system design problems
  const [sdSearchQuery, setSDSearchQuery] = useState('')
  const [isSDSearchFocused, setIsSDSearchFocused] = useState(false)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)

  // YouTube import modal state
  const [isYouTubeImportOpen, setIsYouTubeImportOpen] = useState(false)

  // SD source link modal state
  const [isSDSourceLinkOpen, setIsSDSourceLinkOpen] = useState(false)
  const [pendingSDProblem, setPendingSDProblem] =
    useState<SystemDesignProblem | null>(null)
  const [editingSDSourceItemId, setEditingSDSourceItemId] = useState<
    string | null
  >(null)

  // Check if SD problem already has this source linked
  const sdProblemIdForSourceCheck =
    pendingSDProblem?.id ??
    (editingSDSourceItemId
      ? items.find((i) => i.id === editingSDSourceItemId)?.contentId
      : null) ??
    undefined

  const { data: existingSDSource, isLoading: sdSourceLoading } =
    trpc.admin.content.getSystemDesignProblemSource.useQuery(
      { problemId: sdProblemIdForSourceCheck!, sourceId },
      { enabled: !!sdProblemIdForSourceCheck && isSDSourceLinkOpen },
    )

  // Drag state for reordering, keyed by mixed-list key (`container:<id>` / `item:<id>`)
  const [draggedKey, setDraggedKey] = useState<string | null>(null)

  const keyOf = (entry: (typeof mixedChildren)[number]) =>
    `${entry.type}:${entry.value.id}`

  // Title editing state
  const [editingTitleItemId, setEditingTitleItemId] = useState<string | null>(
    null,
  )
  const [titleEditValue, setTitleEditValue] = useState('')

  // Only one row is ever in title-edit mode, so a single ref suffices. It has
  // to be a stable ref rather than an inline `ref={(el) => el?.focus()}`
  // callback: a fresh callback identity each render makes React detach and
  // re-attach the ref on *every* re-render, re-focusing the input. With an
  // `onBlur` that saves, that focus churn interleaves with saves — any
  // unrelated re-render while the editor is open (drag state, a tRPC refetch,
  // the icon popover) would yank the caret back. This effect fires only when
  // editing actually moves to a different row.
  const titleInputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (editingTitleItemId !== null) titleInputRef.current?.focus()
  }, [editingTitleItemId])

  // Icon editing state
  const [editingIconItemId, setEditingIconItemId] = useState<string | null>(
    null,
  )
  const [iconEditValue, setIconEditValue] = useState('')

  // Search results
  const searchResults = useMemo(() => {
    if (searchQuery.length < minSearchLength) return []
    return search(searchQuery)
  }, [searchQuery, search, minSearchLength])

  // Filter out already-added problems
  const filteredResults = useMemo(() => {
    const existingProblemIds = new Set(
      items.filter((i) => i.contentType === 'PROBLEM').map((i) => i.contentId),
    )
    return searchResults.filter(
      (result) => !existingProblemIds.has(result.item.id),
    )
  }, [searchResults, items])

  // Task search results
  const taskSearchResults = useMemo(() => {
    if (taskSearchQuery.length < minTaskSearchLength) return []
    return searchTasks(taskSearchQuery)
  }, [taskSearchQuery, searchTasks, minTaskSearchLength])

  // Filter out already-added tasks
  const filteredTaskResults = useMemo(() => {
    const existingTaskIds = new Set(
      items.filter((i) => i.contentType === 'TASK').map((i) => i.contentId),
    )
    return taskSearchResults.filter(
      (result) => !existingTaskIds.has(result.item.id),
    )
  }, [taskSearchResults, items])

  // System design search results
  const sdSearchResults = useMemo(() => {
    if (sdSearchQuery.length < minSDSearchLength) return []
    return searchSD(sdSearchQuery)
  }, [sdSearchQuery, searchSD, minSDSearchLength])

  // Filter out already-added system design problems
  const filteredSDResults = useMemo(() => {
    const existingSDIds = new Set(
      items
        .filter((i) => i.contentType === 'SYSTEM_DESIGN')
        .map((i) => i.contentId),
    )
    return sdSearchResults.filter(
      (result) => !existingSDIds.has(result.item.id),
    )
  }, [sdSearchResults, items])

  // Add a problem from search
  const handleAddProblem = useCallback(
    async (problem: CoachingQuestion) => {
      if (itemsLoading) {
        toastError(null, ITEMS_LOADING_MESSAGE)
        return
      }
      try {
        await addItem({
          sourceContentId: contentId,
          contentType: 'PROBLEM',
          contentId: problem.id,
          order: mixedChildren.length,
        })
        toastSuccess(`Added "${problem.name}"`)
        setSearchQuery('')
      } catch (err) {
        toastError(err, 'Failed to add item')
      }
    },
    [addItem, contentId, mixedChildren.length, itemsLoading],
  )

  // Open SD source link modal when adding a system design problem
  const handleAddSystemDesign = useCallback((problem: SystemDesignProblem) => {
    setPendingSDProblem(problem)
    setEditingSDSourceItemId(null)
    setIsSDSourceLinkOpen(true)
    setSDSearchQuery('')
  }, [])

  // Handle SD source link modal submit (create or update)
  const handleSDSourceLinkSubmit = useCallback(
    async (data: SDSourceLinkSubmitData) => {
      // Only the add flow (pendingSDProblem) appends a membership row.
      if (pendingSDProblem && itemsLoading) {
        toastError(null, ITEMS_LOADING_MESSAGE)
        return
      }

      const problemId =
        pendingSDProblem?.id ??
        items.find((i) => i.id === editingSDSourceItemId)?.contentId

      if (!problemId) return

      // Create or update the SystemDesignProblemSource
      if (existingSDSource?.source) {
        // Update existing - sourceId here is the junction table row ID
        await updateSDSourceMutation.mutateAsync({
          problemId,
          sourceId: existingSDSource.source.id,
          // Raw, not `|| undefined`: the mutation reads undefined as "leave
          // this column alone", so a cleared title used to report success and
          // change nothing. The server trims and falls back to 'Untitled'.
          title: data.title,
          externalId: data.externalId || null,
          premiumRequired: data.premiumRequired,
          url: data.url || null,
          editorialUrl: data.editorialUrl || null,
          // Only when the admin actually moved the mark. Echoing the modal's
          // prefill back re-runs the server's clear-then-set, so a stale `true`
          // steals the mark back from whoever moved it while the modal was
          // open, and a stale `false` clears one set since it loaded. The
          // modal reports the delta because only it knows what it prefilled
          // from; the query cache here moves underneath an open modal.
          ...(data.isOriginalChanged ? { isOriginal: data.isOriginal } : {}),
        })
      } else {
        // Create new - sourceId here is the actual source.id from URL params
        const created = await createSDSourceMutation.mutateAsync({
          problemId,
          sourceId,
          title: data.title,
          externalId: data.externalId || null,
          premiumRequired: data.premiumRequired,
          url: data.url || null,
          editorialUrl: data.editorialUrl || null,
        })

        // `isOriginal` is settable only on update: marking one clears whichever
        // row already holds the mark, and that swap lives in the update path.
        if (data.isOriginal && created.source) {
          await updateSDSourceMutation.mutateAsync({
            problemId,
            sourceId: created.source.id,
            isOriginal: true,
          })
        }
      }

      // If this was an add flow (not edit), also add the content item
      if (pendingSDProblem) {
        await addItem({
          sourceContentId: contentId,
          contentType: 'SYSTEM_DESIGN',
          contentId: problemId,
          order: mixedChildren.length,
        })
        toastSuccess(`Added "${pendingSDProblem.title}"`)
      } else {
        toastSuccess('Source details updated')
      }

      setPendingSDProblem(null)
      setEditingSDSourceItemId(null)
    },
    [
      pendingSDProblem,
      editingSDSourceItemId,
      existingSDSource,
      sourceId,
      contentId,
      items,
      addItem,
      createSDSourceMutation,
      updateSDSourceMutation,
      mixedChildren.length,
      itemsLoading,
    ],
  )

  // Open SD source link modal in edit mode for an existing item
  const handleEditSDSource = useCallback((itemId: string) => {
    setEditingSDSourceItemId(itemId)
    setPendingSDProblem(null)
    setIsSDSourceLinkOpen(true)
  }, [])

  // Add an existing task from search
  const handleAddTask = useCallback(
    async (task: Task) => {
      if (itemsLoading) {
        toastError(null, ITEMS_LOADING_MESSAGE)
        return
      }
      try {
        await addItem({
          sourceContentId: contentId,
          contentType: 'TASK',
          contentId: task.id,
          order: mixedChildren.length,
        })
        toastSuccess(`Added "${task.title}"`)
        setTaskSearchQuery('')
      } catch (err) {
        toastError(err, 'Failed to add task')
      }
    },
    [addItem, contentId, mixedChildren.length, itemsLoading],
  )

  // Create a new task and add it to source content
  const handleCreateTask = useCallback(
    async (data: TaskFormData): Promise<Task> => {
      if (itemsLoading) {
        // Throw before creating the task so no orphan Task row is left
        // behind; the task modal surfaces the error and stays open.
        toastError(null, ITEMS_LOADING_MESSAGE)
        throw new Error(ITEMS_LOADING_MESSAGE)
      }
      const created = await createTask(data)
      // After creating, add it as an item to this source content
      await addItem({
        sourceContentId: contentId,
        contentType: 'TASK',
        contentId: created.id,
        order: mixedChildren.length,
      })
      await refreshTasks()
      toastSuccess(`Created and added "${created.title}"`)
      return created
    },
    [
      createTask,
      addItem,
      contentId,
      mixedChildren.length,
      refreshTasks,
      itemsLoading,
    ],
  )

  // Handle task modal submit (create or edit)
  const handleTaskModalSubmit = useCallback(
    async (data: TaskFormData): Promise<Task> => {
      if (editingTask) {
        // Edit mode
        const updated = await updateTask(editingTask.id, data)
        await refreshTasks()
        await refreshItems() // Refresh items to show updated task title
        toastSuccess(`Updated "${updated.title}"`)
        return updated
      } else {
        // Create mode - add to source content
        return handleCreateTask(data)
      }
    },
    [editingTask, updateTask, refreshTasks, refreshItems, handleCreateTask],
  )

  // Open edit modal for a task
  const handleEditTask = useCallback(
    (taskId: string) => {
      const taskToEdit = tasks.find((t) => t.id === taskId)
      if (taskToEdit) {
        setEditingTask(taskToEdit)
        setIsTaskModalOpen(true)
      }
    },
    [tasks],
  )

  // Close task modal and reset state
  const handleCloseTaskModal = useCallback(() => {
    setIsTaskModalOpen(false)
    setEditingTask(null)
  }, [])

  // Delete an item
  const handleDeleteItem = useCallback(
    async (item: SourceContentItem) => {
      if (!confirm('Delete this item?')) return
      try {
        await deleteItem(item.id)
        toastSuccess('Item deleted')
      } catch (err) {
        toastError(err, 'Failed to delete item')
      }
    },
    [deleteItem],
  )

  // Delete a child (sub-container). The server refuses while live child
  // CONTAINERS still exist under it — attached items do not block deletion.
  // Surface that message.
  const deleteContentMutation =
    trpc.admin.content.deleteSourceContent.useMutation({
      onSuccess: () => {
        utils.admin.content.getSourceContent.invalidate()
        utils.admin.content.listSourceContent.invalidate()
        // Deleting the last container of a kind changes the suggestion set.
        utils.admin.content.listSourceContentKinds.invalidate()
      },
    })

  const handleDeleteChildContainer = useCallback(
    async (child: SourceContentChild) => {
      if (!confirm(`Delete "${child.title}"?`)) return
      try {
        await deleteContentMutation.mutateAsync({ id: child.id })
        toastSuccess('Container deleted')
      } catch (err) {
        toastError(err, 'Failed to delete container')
      }
    },
    [deleteContentMutation],
  )

  // Start editing icon for an item
  const handleStartIconEdit = useCallback((item: SourceContentItem) => {
    setEditingIconItemId(item.id)
    setIconEditValue(item.icon || '')
  }, [])

  // Save icon edit
  const handleSaveIcon = useCallback(
    async (itemId: string) => {
      try {
        await updateItem(itemId, { icon: iconEditValue.trim() || null })
        toastSuccess('Icon updated')
        // Only close the row this save belongs to. The editor saves on blur,
        // so clicking straight from row A to row B starts A's save and then
        // opens B — an unconditional reset would resolve afterwards and shut
        // B, discarding the value it had just prefilled. The edit value needs
        // no reset: handleStartIconEdit prefills it every time an editor
        // opens, so it is always written before it is read.
        setEditingIconItemId((cur) => (cur === itemId ? null : cur))
      } catch (err) {
        toastError(err, 'Failed to update icon')
      }
    },
    [updateItem, iconEditValue],
  )

  // Cancel icon edit
  const handleCancelIconEdit = useCallback(() => {
    setEditingIconItemId(null)
    setIconEditValue('')
  }, [])

  // Start editing title for an item
  const handleStartTitleEdit = useCallback((item: SourceContentItem) => {
    setEditingTitleItemId(item.id)
    const title =
      item.problem?.title ||
      item.codingExercise?.title ||
      item.systemDesignProblem?.title ||
      item.task?.title ||
      item.title ||
      ''
    setTitleEditValue(title)
  }, [])

  // Save title edit
  const handleSaveTitle = useCallback(
    async (itemId: string) => {
      try {
        await updateItem(itemId, { title: titleEditValue.trim() })
        // Only close the row this save belongs to — see handleSaveIcon.
        setEditingTitleItemId((cur) => (cur === itemId ? null : cur))
      } catch (err) {
        toastError(err, 'Failed to update title')
      }
    },
    [updateItem, titleEditValue],
  )

  // Cancel title edit
  const handleCancelTitleEdit = useCallback(() => {
    setEditingTitleItemId(null)
    setTitleEditValue('')
  }, [])

  // Drag and drop handlers, operating on the mixed container/item sequence
  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    setDraggedKey(key)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetKey: string) => {
      e.preventDefault()
      const sourceKey = draggedKey
      setDraggedKey(null)
      if (isReordering) return
      if (itemsLoading) {
        // mixedChildren omits item siblings until the items query settles;
        // submitting that partial sequence would just earn a server CONFLICT.
        toastError(null, ITEMS_LOADING_MESSAGE)
        return
      }
      if (!sourceKey || sourceKey === targetKey || !content) return
      const sequence = mixedChildren.map((entry) => ({
        type: entry.type,
        id: entry.value.id,
      }))
      const from = sequence.findIndex((c) => `${c.type}:${c.id}` === sourceKey)
      const to = sequence.findIndex((c) => `${c.type}:${c.id}` === targetKey)
      if (from === -1 || to === -1) return
      const [moved] = sequence.splice(from, 1)
      sequence.splice(to, 0, moved)
      try {
        await reorderChildren({
          sourceId,
          parentId: contentId,
          children: sequence,
        })
        toastSuccess('Reordered')
      } catch (err) {
        toastError(err, 'Failed to reorder')
        invalidateChildViews()
      }
    },
    [
      draggedKey,
      mixedChildren,
      content,
      reorderChildren,
      sourceId,
      contentId,
      invalidateChildViews,
      isReordering,
      itemsLoading,
    ],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null)
  }, [])

  // Get display title for an item
  const getItemDisplayTitle = (item: SourceContentItem) => {
    if (item.problem) {
      return item.problem.title
    }
    if (item.codingExercise) {
      return item.codingExercise.title
    }
    if (item.systemDesignProblem) {
      return item.systemDesignProblem.title
    }
    if (item.task) {
      return item.task.title
    }
    if (item.title) {
      return item.title
    }
    return `${item.contentType} (${item.contentId?.slice(0, 8) || 'external'}...)`
  }

  const contentTypeLabel =
    SOURCE_CONTENT_TYPES.find((t) => t.value === content?.kind)?.label ||
    content?.kind

  // Helper to get task category label
  const getTaskCategoryLabel = (category: string | null) => {
    if (!category) return null
    const cat = TASK_CATEGORIES.find((c) => c.value === category)
    return cat?.label || category
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading content...</span>
        </div>
      </div>
    )
  }

  if (error || !content) {
    return (
      <div className="min-h-screen">
        <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
          <div className="mx-auto max-w-4xl px-6 py-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/admin/sources/${sourceId}`)}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Source
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-4xl px-6 py-6">
          <div className="py-16 text-center">
            <h3 className="text-foreground mb-2 text-lg font-medium">
              {error || 'Content not found'}
            </h3>
            <Button
              onClick={() => navigate(`/admin/sources/${sourceId}`)}
              variant="outline"
            >
              Back to Source
            </Button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/admin/sources/${sourceId}`)}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <div className="flex-1">
              <h1 className="text-foreground text-xl font-bold">
                {content.title}
              </h1>
              <p className="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
                <button
                  type="button"
                  onClick={() => navigate(`/admin/sources/${sourceId}`)}
                  className="hover:text-foreground hover:underline"
                >
                  {sourceName}
                </button>
                {ancestors.map((ancestor) => (
                  <span key={ancestor.id} className="flex items-center gap-1">
                    <ChevronRight size={12} />
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/admin/sources/${sourceId}/content/${ancestor.id}`,
                        )
                      }
                      className="hover:text-foreground hover:underline"
                    >
                      {ancestor.title}
                    </button>
                  </span>
                ))}
                <span className="flex items-center gap-1">
                  <ChevronRight size={12} />
                  {contentTypeLabel} • {mixedChildren.length}{' '}
                  {mixedChildren.length === 1 ? 'child' : 'children'}
                </span>
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddChildOpen(true)}
            >
              <Plus size={14} className="mr-1" />
              Add Sub-container
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsYouTubeImportOpen(true)}
            >
              <Video size={14} className="mr-1" />
              Import YouTube Playlist
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-6 py-6">
        {/* Description */}
        {content.description && (
          <div className="bg-muted/30 mb-6 rounded-lg p-4">
            <p className="text-muted-foreground text-sm">
              {content.description}
            </p>
          </div>
        )}

        {/* Fuzzy Search to Add Problems */}
        <div className="mb-6">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground font-semibold">Add Problem</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  navigate(
                    `/admin/problems/new?linkToSourceContent=${contentId}&returnTo=${encodeURIComponent(`/admin/sources/${sourceId}/content/${contentId}`)}`,
                  )
                }
              >
                <Plus size={14} className="mr-1" />
                New Problem
              </Button>
            </div>
            <Command
              className="border-border rounded-lg border"
              shouldFilter={false}
            >
              <CommandInput
                placeholder={
                  questionsLoading
                    ? 'Loading problems...'
                    : 'Search problems by name or number...'
                }
                value={searchQuery}
                onValueChange={setSearchQuery}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              />
              {(isSearchFocused || searchQuery.length > 0) && (
                <CommandList className="max-h-[300px]">
                  {searchQuery.length > 0 &&
                    searchQuery.length < minSearchLength && (
                      <CommandEmpty>
                        Type {minSearchLength - searchQuery.length} more
                        character
                        {minSearchLength - searchQuery.length !== 1 ? 's' : ''}{' '}
                        to search...
                      </CommandEmpty>
                    )}
                  {searchQuery.length >= minSearchLength &&
                    filteredResults.length === 0 && (
                      <CommandEmpty>No problems found.</CommandEmpty>
                    )}
                  {filteredResults.length > 0 && (
                    <CommandGroup heading="Problems">
                      {filteredResults.map((result) => {
                        const allSources = result.item.allSources || []
                        // Check if all titles are the same
                        const uniqueTitles = new Set(
                          allSources.map((s) => s.title),
                        )
                        const titlesAreSame = uniqueTitles.size <= 1
                        return (
                          <CommandItem
                            key={result.item.id}
                            value={`${result.item.externalId || ''} ${result.item.name}`}
                            onSelect={() => handleAddProblem(result.item)}
                            className="cursor-pointer"
                          >
                            <div className="flex flex-1 items-center gap-2 overflow-hidden">
                              {/* Show all sources with icons, external IDs, and titles if different */}
                              <div className="flex flex-shrink-0 items-center">
                                {allSources.length > 0 ? (
                                  allSources.map((src, idx) => (
                                    <div
                                      key={src.id}
                                      className="flex items-center"
                                    >
                                      {idx > 0 && (
                                        <span className="text-muted-foreground mx-1.5 text-xs">
                                          |
                                        </span>
                                      )}
                                      <SearchResultIcon
                                        icon={src.sourceIcon}
                                        sourceName={src.source}
                                      />
                                      {src.externalId && (
                                        <span className="text-muted-foreground ml-0.5 text-xs">
                                          #{src.externalId}
                                        </span>
                                      )}
                                      {/* Show title inline if titles differ */}
                                      {!titlesAreSame && src.title && (
                                        <span className="ml-1 max-w-[120px] truncate text-sm">
                                          {src.title}
                                        </span>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <>
                                    <SearchResultIcon
                                      icon={result.item.sourceIcon}
                                      sourceName={result.item.source || ''}
                                    />
                                    {result.item.externalId && (
                                      <span className="text-muted-foreground ml-0.5 text-xs">
                                        #{result.item.externalId}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              {/* Problem name - only show once if all titles are same */}
                              {(titlesAreSame || allSources.length === 0) && (
                                <span className="flex-1 truncate">
                                  {result.item.name}
                                </span>
                              )}
                            </div>
                            {result.item.difficulty && (
                              <span
                                className={`ml-2 flex-shrink-0 rounded px-1.5 py-0.5 text-xs ${getDifficultyColorClass(result.item.difficulty)}`}
                              >
                                {getDifficultyLabel(result.item.difficulty)}
                              </span>
                            )}
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              )}
            </Command>
            <p className="text-muted-foreground mt-2 text-xs">
              <Search size={12} className="mr-1 inline" />
              Type to fuzzy search by problem name or LeetCode number
            </p>
          </div>
        </div>

        {/* Task Search and Create */}
        <div className="mb-6">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground font-semibold">Add Task</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsTaskModalOpen(true)}
              >
                <Plus size={14} className="mr-1" />
                New Task
              </Button>
            </div>
            <Command
              className="border-border rounded-lg border"
              shouldFilter={false}
            >
              <CommandInput
                placeholder={
                  tasksLoading ? 'Loading tasks...' : 'Search existing tasks...'
                }
                value={taskSearchQuery}
                onValueChange={setTaskSearchQuery}
                onFocus={() => setIsTaskSearchFocused(true)}
                onBlur={() =>
                  setTimeout(() => setIsTaskSearchFocused(false), 200)
                }
              />
              {(isTaskSearchFocused || taskSearchQuery.length > 0) && (
                <CommandList className="max-h-[300px]">
                  {taskSearchQuery.length > 0 &&
                    taskSearchQuery.length < minTaskSearchLength && (
                      <CommandEmpty>
                        Type {minTaskSearchLength - taskSearchQuery.length} more
                        character
                        {minTaskSearchLength - taskSearchQuery.length !== 1
                          ? 's'
                          : ''}{' '}
                        to search...
                      </CommandEmpty>
                    )}
                  {taskSearchQuery.length >= minTaskSearchLength &&
                    filteredTaskResults.length === 0 && (
                      <CommandEmpty>No tasks found.</CommandEmpty>
                    )}
                  {filteredTaskResults.length > 0 && (
                    <CommandGroup heading="Tasks">
                      {filteredTaskResults.map((result) => (
                        <CommandItem
                          key={result.item.id}
                          value={result.item.title}
                          onSelect={() => handleAddTask(result.item)}
                          className="cursor-pointer"
                        >
                          <CheckSquare
                            size={14}
                            className="mr-2 flex-shrink-0"
                          />
                          <span className="flex-1 truncate">
                            {result.item.title}
                          </span>
                          {result.item.category && (
                            <span className="bg-muted text-muted-foreground ml-2 rounded px-1.5 py-0.5 text-xs">
                              {getTaskCategoryLabel(result.item.category)}
                            </span>
                          )}
                          {result.item.sourceId === sourceId && (
                            <span className="text-primary ml-1 text-xs">
                              (this source)
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              )}
            </Command>
            <p className="text-muted-foreground mt-2 text-xs">
              <Search size={12} className="mr-1 inline" />
              Search existing tasks or create a new one
            </p>
          </div>
        </div>

        {/* System Design Problem Search */}
        <div className="mb-6">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground font-semibold">
                Add System Design Problem
              </h2>
            </div>
            <Command
              className="border-border rounded-lg border"
              shouldFilter={false}
            >
              <CommandInput
                placeholder={
                  sdLoading
                    ? 'Loading system design problems...'
                    : 'Search system design problems...'
                }
                value={sdSearchQuery}
                onValueChange={setSDSearchQuery}
                onFocus={() => setIsSDSearchFocused(true)}
                onBlur={() =>
                  setTimeout(() => setIsSDSearchFocused(false), 200)
                }
              />
              {(isSDSearchFocused || sdSearchQuery.length > 0) && (
                <CommandList className="max-h-[300px]">
                  {sdSearchQuery.length > 0 &&
                    sdSearchQuery.length < minSDSearchLength && (
                      <CommandEmpty>
                        Type {minSDSearchLength - sdSearchQuery.length} more
                        character
                        {minSDSearchLength - sdSearchQuery.length !== 1
                          ? 's'
                          : ''}{' '}
                        to search...
                      </CommandEmpty>
                    )}
                  {sdSearchQuery.length >= minSDSearchLength &&
                    filteredSDResults.length === 0 && (
                      <CommandEmpty>
                        No system design problems found.
                      </CommandEmpty>
                    )}
                  {filteredSDResults.length > 0 && (
                    <CommandGroup heading="System Design Problems">
                      {filteredSDResults.map((result) => (
                        <CommandItem
                          key={result.item.id}
                          value={result.item.title}
                          onSelect={() => handleAddSystemDesign(result.item)}
                          className="cursor-pointer"
                        >
                          <Server size={14} className="mr-2 flex-shrink-0" />
                          <span className="flex-1 truncate">
                            {result.item.title}
                          </span>
                          {result.item.difficulty && (
                            <span
                              className={`ml-2 flex-shrink-0 rounded px-1.5 py-0.5 text-xs ${getDifficultyColorClass(result.item.difficulty)}`}
                            >
                              {getDifficultyLabel(result.item.difficulty)}
                            </span>
                          )}
                          {result.item.companyTags.length > 0 && (
                            <span className="bg-muted text-muted-foreground ml-1 flex-shrink-0 rounded px-1.5 py-0.5 text-xs">
                              {result.item.companyTags
                                .map((t) => t.name)
                                .join(', ')}
                            </span>
                          )}
                          {result.item.topics.length > 0 && (
                            <span className="text-muted-foreground ml-1 flex-shrink-0 text-xs">
                              {result.item.topics.slice(0, 2).join(', ')}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              )}
            </Command>
            <p className="text-muted-foreground mt-2 text-xs">
              <Search size={12} className="mr-1 inline" />
              Search existing system design problems by title, company, or topic
            </p>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border flex items-center justify-between border-b p-4">
            <h2 className="text-foreground font-semibold">
              Children ({mixedChildren.length})
            </h2>
            {itemsLoading && (
              <RefreshCw
                size={16}
                className="text-muted-foreground animate-spin"
              />
            )}
          </div>

          {mixedChildren.length === 0 ? (
            <div className="py-12 text-center">
              <Code size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">
                No children yet. Attach problems with the search above, or add a
                sub-container.
              </p>
            </div>
          ) : (
            <div className="divide-border divide-y">
              {mixedChildren.map((entry) => {
                const key = keyOf(entry)
                if (entry.type === 'container') {
                  const child = entry.value
                  return (
                    <ContainerChildRow
                      key={key}
                      child={child}
                      isDragged={draggedKey === key}
                      onNavigate={() =>
                        navigate(
                          `/admin/sources/${sourceId}/content/${child.id}`,
                        )
                      }
                      onEdit={() => {
                        const full = contents.find((c) => c.id === child.id)
                        if (full) {
                          setEditingChild(full)
                        } else {
                          toastError(
                            null,
                            contentsLoading
                              ? 'Still loading — try again'
                              : 'Container not found',
                          )
                        }
                      }}
                      onDelete={() => handleDeleteChildContainer(child)}
                      dragProps={{
                        onDragStart: (e) => handleDragStart(e, key),
                        onDragOver: handleDragOver,
                        onDrop: (e) => handleDrop(e, key),
                        onDragEnd: handleDragEnd,
                      }}
                    />
                  )
                }
                const item = entry.value
                const Icon = getItemIcon(item)
                const typeLabel =
                  SOURCE_CONTENT_ITEM_TYPES.find(
                    (t) => t.value === item.contentType,
                  )?.label || item.contentType

                // Get full problem data if this is a PROBLEM item
                const problem =
                  item.contentType === 'PROBLEM' && item.contentId
                    ? problemsById.get(item.contentId)
                    : null

                return (
                  <div
                    key={key}
                    draggable
                    onDragStart={(e) => handleDragStart(e, key)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, key)}
                    onDragEnd={handleDragEnd}
                    className={`group flex items-center gap-3 p-4 transition-colors ${
                      draggedKey === key
                        ? 'bg-muted/50 opacity-50'
                        : 'hover:bg-muted/30'
                    }`}
                  >
                    {/* Drag handle */}
                    <div
                      className={cn(
                        REVEAL_ON_HOVER,
                        'text-muted-foreground cursor-grab',
                      )}
                    >
                      <GripVertical size={16} />
                    </div>

                    {/* Icon - the item's own authored icon, else the problem's source icon, else the type icon */}
                    <SourceContentItemIcon
                      itemIcon={item.icon}
                      problemIcon={problem?.sourceIcon}
                      FallbackIcon={Icon}
                    />

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* External ID */}
                        {problem?.externalId && (
                          <span className="text-muted-foreground text-sm">
                            #{problem.externalId}
                          </span>
                        )}
                        {/* Name - click to edit for non-problem items */}
                        {editingTitleItemId === item.id ? (
                          <input
                            type="text"
                            value={titleEditValue}
                            onChange={(e) => setTitleEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveTitle(item.id)
                              if (e.key === 'Escape') handleCancelTitleEdit()
                            }}
                            onBlur={() => handleSaveTitle(item.id)}
                            className="text-foreground bg-transparent min-w-0 flex-1 border-none p-0 font-medium outline-none ring-1 ring-blue-500 rounded px-1"
                            ref={titleInputRef}
                          />
                        ) : problem ? (
                          // Problem titles aren't editable here — plain text,
                          // not a non-functional button in the tab order.
                          <span className="text-foreground truncate font-medium">
                            {problem.name}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-foreground cursor-text truncate text-left font-medium hover:ring-1 hover:ring-muted-foreground/30 hover:rounded hover:px-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStartTitleEdit(item)
                            }}
                          >
                            {getItemDisplayTitle(item)}
                          </button>
                        )}
                        {/* Difficulty badge */}
                        {problem?.difficulty && (
                          <span
                            className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs ${getDifficultyColorClass(problem.difficulty)}`}
                          >
                            {getDifficultyLabel(problem.difficulty)}
                          </span>
                        )}
                        {/* Type label for non-problems */}
                        {!problem && (
                          <span className="bg-muted text-muted-foreground flex-shrink-0 rounded px-1.5 py-0.5 text-xs">
                            {typeLabel}
                          </span>
                        )}
                      </div>
                      {/* Approaches */}
                      {problem?.approaches && problem.approaches.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {problem.approaches.map((approach, idx) => {
                            const config = approach.solutionType
                              ? SOLUTION_TYPE_CONFIG[approach.solutionType]
                              : null
                            const SolutionIcon = config?.icon
                            const strategies = approach.strategies || []
                            return (
                              <div
                                key={`${approach.solutionType}-${idx}`}
                                className="bg-muted/50 flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
                              >
                                {SolutionIcon && (
                                  <SolutionIcon
                                    size={10}
                                    className={config?.color}
                                  />
                                )}
                                {strategies.length > 0 && (
                                  <span className="text-muted-foreground">
                                    {strategies.map((c) => c.name).join(', ')}
                                  </span>
                                )}
                                {strategies.length === 0 && config && (
                                  <span className="text-muted-foreground">
                                    {config.label}
                                  </span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {/* Description for non-problems */}
                      {!problem && item.description && (
                        <p className="text-muted-foreground mt-0.5 truncate text-sm">
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* Duration */}
                    {item.durationMinutes != null &&
                      item.durationMinutes > 0 && (
                        <span className="text-muted-foreground text-sm">
                          {item.durationMinutes} min
                        </span>
                      )}

                    {/* External link */}
                    {(item.url || problem?.url) && (
                      <a
                        href={item.url || problem?.url || ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}

                    {/* Edit button for tasks */}
                    {item.contentType === 'TASK' && item.contentId && (
                      <button
                        type="button"
                        onClick={() => handleEditTask(item.contentId!)}
                        className={cn(
                          REVEAL_ON_HOVER,
                          'text-muted-foreground hover:text-primary',
                        )}
                        title="Edit task"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}

                    {/* Edit source link button for system design problems */}
                    {item.contentType === 'SYSTEM_DESIGN' && item.contentId && (
                      <button
                        type="button"
                        onClick={() => handleEditSDSource(item.id)}
                        className={cn(
                          REVEAL_ON_HOVER,
                          'text-muted-foreground hover:text-primary',
                        )}
                        title="Edit source link"
                      >
                        <Link size={14} />
                      </button>
                    )}

                    {/* Icon edit */}
                    {editingIconItemId === item.id ? (
                      <div
                        className="relative"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') handleCancelIconEdit()
                        }}
                      >
                        <div className="border-border bg-popover absolute top-full right-0 z-20 mt-1 w-72 rounded-md border p-3 shadow-md">
                          <IconField
                            label="Icon"
                            value={iconEditValue}
                            onChange={setIconEditValue}
                          />
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveIcon(item.id)}
                              className="text-muted-foreground hover:text-primary text-xs"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelIconEdit}
                              className="text-muted-foreground hover:text-destructive text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleStartIconEdit(item)}
                        className={cn(
                          REVEAL_ON_HOVER,
                          'text-muted-foreground hover:text-primary',
                        )}
                        title={item.icon ? `Icon: ${item.icon}` : 'Set icon'}
                      >
                        <Image size={14} />
                      </button>
                    )}

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item)}
                      title="Delete item"
                      aria-label={`Delete ${getItemDisplayTitle(item)}`}
                      className={cn(
                        REVEAL_ON_HOVER,
                        'text-muted-foreground hover:text-destructive',
                      )}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Help text */}
        <p className="text-muted-foreground mt-4 text-center text-xs">
          Drag items to reorder • Click delete to remove
        </p>
      </main>

      {/* Task Editor Modal */}
      <TaskEditorModal
        isOpen={isTaskModalOpen}
        onClose={handleCloseTaskModal}
        onSubmit={handleTaskModalSubmit}
        task={editingTask}
        initialSourceId={editingTask ? undefined : sourceId}
        initialSourceContentId={editingTask ? undefined : contentId}
      />

      {/* YouTube Import Modal */}
      <YouTubeImportModal
        open={isYouTubeImportOpen}
        onOpenChange={setIsYouTubeImportOpen}
        sourceContentId={contentId}
        onImported={refreshItems}
      />

      {/* SD Source Link Modal */}
      <SDSourceLinkModal
        isOpen={isSDSourceLinkOpen}
        onClose={() => {
          setIsSDSourceLinkOpen(false)
          setPendingSDProblem(null)
          setEditingSDSourceItemId(null)
        }}
        onSubmit={handleSDSourceLinkSubmit}
        problemTitle={
          pendingSDProblem?.title ??
          items.find((i) => i.id === editingSDSourceItemId)?.systemDesignProblem
            ?.title ??
          ''
        }
        sourceName={sourceName}
        existingSource={existingSDSource?.source ?? null}
        isLoading={sdSourceLoading && isSDSourceLinkOpen}
      />

      {/* Add/Edit Sub-container Modal */}
      <SourceContentModal
        open={isAddChildOpen || editingChild !== null}
        sourceId={sourceId}
        editingContent={editingChild}
        defaultParentId={contentId}
        allContents={contents}
        onClose={() => {
          setIsAddChildOpen(false)
          setEditingChild(null)
        }}
      />
    </div>
  )
}
